import type { XYCoord } from "react-dnd";

import type { OccupiedSpace } from "legacy/constants/editorConstants";
import {
  GridDefaults,
  MIN_WIDGET_SIZE,
} from "legacy/constants/WidgetConstants";
import type { WidgetType } from "legacy/constants/WidgetConstants";

import areIntersecting from "./areIntersecting";
import isWithin from "./isWithin";
import type { Rect } from "./ResizableUtils";

export const CantFit = undefined;

/**
 * "Get the available space for a widget in a drop zone, given the desired location, the occupied
 * spaces, and whether the widget can shrink to fit."
 *
 * @param {Rect} parent - The parent widget's rect
 * @param {Rect} desiredLocation - The location of the widget being dragged to
 * @param {string} widgetId - The id of the widget being dragged
 * @param {WidgetType} widgetType - The type of widget being dragged
 * @param {OccupiedSpace[]} [occupied] - An array of objects that represent the occupied space in the drop zone
 * @param {boolean} [cannotMoveToOriginalPosition] - true when the widget is being moved to a new location.
 * @param {boolean} [canShrink] - If true, the widget can be shrunk to fit within the parent.
 * @param {XYCoord} [mouseOffset] - The offset of the mouse from the top left corner of the widget.
 * @param {Record<string, "left" | "right" | "top" | "bottom">} [additonalShrinkableWidgetIds] - Widget IDs that can be shrunk to make space
 * @returns An object with rect (CantFit or Rect) and additionalShrinks
 */
const getAvailableRectInDropZone = (
  parent: Rect,
  desiredLocation: Rect,
  widgetId: string,
  widgetType: WidgetType,
  occupied?: OccupiedSpace[],
  cannotMoveToOriginalPosition?: boolean,
  canShrink?: boolean,
  mouseOffset?: XYCoord,
  additonalShrinkableWidgetIds?: Record<
    string,
    "right" | "left" | "bottom" | "top"
  >,
): {
  rect: typeof CantFit | Rect;
  additionalShrinks: {
    [widgetId: string]: {
      left: number;
      width: number;
      top: number;
      height: number;
    };
  };
} => {
  const offset = desiredLocation;
  const { minWidth, minHeight } = getMinSize(offset);

  const additionalShrinks: {
    [widgetId: string]: {
      left: number;
      width: number;
      top: number;
      height: number;
    };
  } = {};

  if (canShrink) {
    // Shrink the widget to fit within the parent
    offset.left = Math.max(offset.left, parent.left);
    offset.right = Math.min(offset.right, parent.right);
    offset.top = Math.max(offset.top, parent.top);
    offset.bottom = Math.min(
      offset.bottom,
      parent.bottom || GridDefaults.DEFAULT_GRID_COLUMNS,
    );
    if (offset.right === offset.left) {
      offset.left -= MIN_WIDGET_SIZE.width;
    }
    if (offset.bottom === offset.top) {
      offset.top -= MIN_WIDGET_SIZE.height;
    }
  } else {
    // Check if the widget is within the parent
    if (!isWithin(offset, parent)) {
      return { rect: CantFit, additionalShrinks };
    }
  }

  if (
    !additonalShrinkableWidgetIds &&
    (offset.right - offset.left < minWidth ||
      offset.bottom - offset.top < minHeight)
  ) {
    return { rect: CantFit, additionalShrinks };
  }

  if (occupied) {
    if (!cannotMoveToOriginalPosition) {
      occupied = occupied.filter((widgetDetails) => {
        return (
          widgetDetails.id !== widgetId && widgetDetails.parentId !== widgetId
        );
      });
    }
  }

  return _getAvailableRectInDropZone(
    widgetType,
    offset,
    occupied,
    canShrink,
    mouseOffset,
    additonalShrinkableWidgetIds,
    additionalShrinks,
  );
};
const _getAvailableRectInDropZone = (
  widgetType: WidgetType,
  offset: Rect,
  occupied?: OccupiedSpace[],
  canShrink?: boolean,
  mouseOffset: XYCoord = {
    x: offset.left + offset.right / 2,
    y: offset.top + offset.bottom / 2,
  },
  additonalShrinkableWidgetIds?: Record<
    string,
    "right" | "left" | "bottom" | "top"
  >,
  additionalShrinks: {
    [widgetId: string]: {
      left: number;
      width: number;
      top: number;
      height: number;
    };
  } = {},
): {
  rect: typeof CantFit | Rect;
  additionalShrinks: {
    [widgetId: string]: {
      left: number;
      width: number;
      top: number;
      height: number;
    };
  };
} => {
  if (occupied) {
    const { minWidth, minHeight } = getMinSize(offset);
    const collisions: OccupiedSpace[] = [];
    for (let i = 0; i < occupied.length; i++) {
      if (areIntersecting(occupied[i], offset)) {
        // If the widget is shrinkable, try to shrink it to make space
        if (additonalShrinkableWidgetIds?.[occupied[i].id]) {
          const shrinkResult = tryToShrinkOccupiedSpace(
            occupied[i],
            offset,
            additonalShrinkableWidgetIds[occupied[i].id],
          );

          if (shrinkResult !== null) {
            // We successfully shrunk the occupied space
            additionalShrinks[occupied[i].id] = shrinkResult.measurements;
            // Update the occupied space with its new dimensions
            occupied[i] = shrinkResult.newRect;
            // Recheck if there's still a collision
            if (!areIntersecting(occupied[i], offset)) {
              continue; // No collision after shrinking, move to next
            }
          }
        }

        // If the widget cannot shrink or still collides after shrinking
        if (!canShrink) {
          return { rect: CantFit, additionalShrinks };
        }
        collisions.push(occupied[i]);
      }

      // If there are more than 5 collisions, it is not worth trying to fit the widget
      if (collisions.length > 5) {
        return { rect: CantFit, additionalShrinks };
      }
    }

    // If there are no collisions, we can fit the widget in the drop zone
    if (collisions.length === 0) {
      return { rect: offset, additionalShrinks };
    }

    const centerOffset = mouseOffset;

    // Recursively check if the widget can fit in the drop zone
    // Comparing the volume/centerPos to achieve the best fit
    const potentialOffsets = [];
    for (let i = 0; i < collisions.length; i++) {
      const collision = collisions[i];

      // Calculate the width that fits in the drop zone
      const offsetCenterX = centerOffset.x;
      const collisionCenterX = (collision.left + collision.right) / 2;
      const shouldGoLeft = offsetCenterX < collisionCenterX;

      const widthThatFits = shouldGoLeft
        ? collision.left - offset.left
        : offset.right - collision.right;
      const newOffsetHorizontal = {
        top: offset.top,
        left: shouldGoLeft ? offset.left : collision.right,
        right: shouldGoLeft ? collision.left : offset.right,
        bottom: offset.bottom,
      };

      // Calculate the height that fits in the drop zone
      const offsetCenterY = centerOffset.y;
      const collisionCenterY = (collision.top + collision.bottom) / 2;
      const shouldGoAbove = offsetCenterY < collisionCenterY;

      const heightThatFits = shouldGoAbove
        ? collision.top - offset.top
        : offset.bottom - collision.bottom;
      const newOffsetVertical = {
        top: shouldGoAbove ? offset.top : collision.bottom,
        left: offset.left,
        right: offset.right,
        bottom: shouldGoAbove ? collision.top : offset.bottom,
      };

      if (
        widthThatFits >= minWidth &&
        !areIntersecting(collision, newOffsetHorizontal)
      ) {
        potentialOffsets.push(
          _getAvailableRectInDropZone(
            widgetType,
            newOffsetHorizontal,
            collisions.filter((c) => c !== collision),
            canShrink,
            mouseOffset,
            additonalShrinkableWidgetIds,
            additionalShrinks,
          ),
        );
      }
      if (
        heightThatFits >= minHeight &&
        !areIntersecting(collision, newOffsetVertical)
      ) {
        potentialOffsets.push(
          _getAvailableRectInDropZone(
            widgetType,
            newOffsetVertical,
            collisions.filter((c) => c !== collision),
            canShrink,
            mouseOffset,
            additonalShrinkableWidgetIds,
            additionalShrinks,
          ),
        );
      }
    }

    // Sort the potential offsets by distance from the desired location
    // and a small percent of the volume of the widget to achieve the best fit
    potentialOffsets.sort((a, b) => {
      if (a.rect === CantFit) return 1;
      if (b.rect === CantFit) return -1;
      const aValue = positionScore(a.rect, mouseOffset);
      const bValue = positionScore(b.rect, mouseOffset);
      return -(aValue - bValue);
    });

    return potentialOffsets.length > 0
      ? potentialOffsets[0]
      : { rect: CantFit, additionalShrinks };
  }
  return { rect: offset, additionalShrinks };
};

/**
 * Try to shrink an occupied space to avoid collision
 * @param occupied The occupied space to shrink
 * @param target The target rect we're trying to fit
 * @param mouseOffset The mouse position that indicates preference direction
 * @returns null if cannot shrink, or object with new rect and measurements
 */
export const tryToShrinkOccupiedSpace = (
  occupied: OccupiedSpace,
  target: Rect,
  shrinkDirection: "right" | "left" | "bottom" | "top",
): {
  newRect: OccupiedSpace;
  measurements: { left: number; width: number; top: number; height: number };
} | null => {
  const MIN_DIMENSION = 2;
  const newRect = { ...occupied };
  let changedSize = false;

  // Check if there's a horizontal collision from the left
  if (
    shrinkDirection === "left" &&
    target.left < occupied.right &&
    target.right > occupied.left
  ) {
    // Calculate the minimum amount needed to shrink from the left
    const horizontalOverlap =
      Math.min(occupied.right, target.right) -
      Math.max(occupied.left, target.left);

    // Only shrink if we're overlapping from the left side
    if (target.right >= occupied.left && target.left <= occupied.left) {
      const newLeft = occupied.left + horizontalOverlap;
      // Ensure we maintain minimum width
      if (occupied.right - newLeft >= MIN_DIMENSION) {
        newRect.left = newLeft;
        changedSize = true;
      }
    }
  }

  // Check if there's a horizontal collision from the right
  if (
    shrinkDirection === "right" &&
    target.left < occupied.right &&
    target.right > occupied.left
  ) {
    // Calculate the minimum amount needed to shrink from the right
    const horizontalOverlap =
      Math.min(occupied.right, target.right) -
      Math.max(occupied.left, target.left);

    // Only shrink if we're overlapping from the right side
    if (target.left <= occupied.right && target.right >= occupied.right) {
      const newRight = occupied.right - horizontalOverlap;
      // Ensure we maintain minimum width
      if (newRight - occupied.left >= MIN_DIMENSION) {
        newRect.right = newRight;
        changedSize = true;
      }
    }
  }

  // Check if there's a vertical collision from the top
  if (
    shrinkDirection === "top" &&
    target.top < occupied.bottom &&
    target.bottom > occupied.top
  ) {
    // Calculate the minimum amount needed to shrink from the top
    const verticalOverlap =
      Math.min(occupied.bottom, target.bottom) -
      Math.max(occupied.top, target.top);

    // Only shrink if we're overlapping from the top side
    if (target.bottom >= occupied.top && target.top <= occupied.top) {
      const newTop = occupied.top + verticalOverlap;
      // Ensure we maintain minimum height
      if (occupied.bottom - newTop >= MIN_DIMENSION) {
        newRect.top = newTop;
        changedSize = true;
      }
    }
  }

  // Check if there's a vertical collision from the bottom
  if (
    shrinkDirection === "bottom" &&
    target.top < occupied.bottom &&
    target.bottom > occupied.top
  ) {
    // Calculate the minimum amount needed to shrink from the bottom
    const verticalOverlap =
      Math.min(occupied.bottom, target.bottom) -
      Math.max(occupied.top, target.top);

    // Only shrink if we're overlapping from the bottom side
    if (target.top <= occupied.bottom && target.bottom >= occupied.bottom) {
      const newBottom = occupied.bottom - verticalOverlap;
      // Ensure we maintain minimum height
      if (newBottom - occupied.top >= MIN_DIMENSION) {
        newRect.bottom = newBottom;
        changedSize = true;
      }
    }
  }

  // Check if shrinking actually helped
  if (!changedSize) {
    return null;
  }

  // Convert to left/width/top/height format
  const measurements = {
    left: newRect.left,
    width: newRect.right - newRect.left,
    top: newRect.top,
    height: newRect.bottom - newRect.top,
  };

  return { newRect, measurements };
};

/**
 * Returns an available space near the desired location within the parent limits.
 * It won't return the desired exact spot given in params (use getAvailableRectInDropZone for that).
 * @param {Rect} parent - The parent widget's rect
 * @param {Rect} desiredLocation - The desired location to place the rect
 * @param {WidgetType} widgetType - Type of widget
 * @param {OccupiedSpace[]} occupied - An array of objects that represent the occupied space in the drop zone
 * @returns The value of the position of the rectangle
 */
export const getNearAvailableRectInDropZone = (
  parent: Rect,
  desiredLocation: Rect,
  widgetType: WidgetType,
  occupied: OccupiedSpace[] = [],
): typeof CantFit | Rect => {
  const centerOffset: XYCoord = {
    x: (desiredLocation.right - desiredLocation.left) / 2,
    y: (desiredLocation.top - desiredLocation.bottom) / 2,
  };

  const { left, top, right, bottom } = desiredLocation;
  const width = right - left;
  const height = bottom - top;
  const center: XYCoord = {
    x: left + centerOffset.x,
    y: top + centerOffset.y,
  };

  const maxRadius = Math.min(
    Math.max(parent.right - parent.left, parent.bottom - parent.top),
    GridDefaults.DEFAULT_GRID_COLUMNS / 3, // A third of the width screen
  );

  const minRadius = Math.min(width, height) / 2;

  for (let radius = minRadius; radius <= maxRadius; radius++) {
    for (let angle = 0; angle < 360; angle += 15) {
      const radians = (angle * Math.PI) / 180;
      const x = Math.round(center.x + radius * Math.cos(radians));
      const y = Math.round(center.y + radius * Math.sin(radians));

      const newRect: Rect = {
        top: y,
        left: x,
        right: x + width,
        bottom: y + height,
      };

      if (isWithin(newRect, parent)) {
        const validSpace = _getAvailableRectInDropZone(
          widgetType,
          newRect,
          occupied,
          false,
        );
        if (validSpace !== CantFit) {
          return validSpace.rect;
        }
      }
    }
  }

  return CantFit;
};

const getMinSize = (currentSize: Rect) => {
  let { height: minHeight, width: minWidth } = MIN_WIDGET_SIZE;

  minWidth = Math.min(
    minWidth,
    Math.max(currentSize.right - currentSize.left, 1),
  );
  minHeight = Math.min(
    minHeight,
    Math.max(currentSize.bottom - currentSize.top, 1),
  );
  return { minWidth, minHeight };
};

/**
 * Returns a number that represents how good of a position/size the rectangle this is open to tweaking
 * @param {Rect} a - Rect - The rectangle to score
 * @param {XYCoord} mouseOffset - The position of the mouse relative to the top left of the viewport.
 * @returns The value of the position of the rectangle.
 */
function positionScore(a: Rect, mouseOffset: XYCoord) {
  const volume = (a.right - a.left) * (a.bottom - a.top);
  const center = {
    x: (a.left + a.right) / 2,
    y: (a.top + a.bottom) / 2,
  };
  const distanceToCenter = Math.sqrt(
    Math.pow(center.x - mouseOffset.x, 2) +
      Math.pow(center.y - mouseOffset.y, 2),
  );
  return Math.pow(volume, 0.5) - distanceToCenter;
}

export default getAvailableRectInDropZone;
